import { queryOptions } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import { SESSION_QUERY_KEY, SESSION_STALE_TIME } from '@/shared/config'

import { fetchSession } from '@/features/auth'
import type { Session } from '@/features/auth'

/**
 * ─── SEAM (filled) ───────────────────────────────────────────────────────────
 * The route guard needs to know who is signed in, and the auth feature is the
 * only thing that knows how to answer. `Session` is re-exported here so routes
 * keep taking the type from the router, unaware a feature owns it.
 *
 * Point this at a different feature and nothing else in the app moves: routes
 * only ever call `requireSession`.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export type { Session }

export const sessionQuery = () =>
  queryOptions({
    queryKey: SESSION_QUERY_KEY,
    staleTime: SESSION_STALE_TIME,
    // A 401 here is a real answer ("not signed in"), not a failure to retry.
    retry: false,
    // Never persisted: `PERSISTED_QUERY_ROOTS` does not list 'session', and it
    // must not — this payload carries an email and subscription data.
    queryFn: ({ signal }) => fetchSession(signal),
  })

/**
 * Access is enforced where the route is declared, never with an early return
 * inside a page component. While this resolves, the router shows the app shell
 * skeleton — a reload must not flash a redirect to login.
 */
export async function requireSession(queryClient: QueryClient, href: string): Promise<Session> {
  const session = await queryClient.ensureQueryData(sessionQuery()).catch(() => null)

  if (session === null) {
    throw redirect({ to: '/login', search: { returnTo: href } })
  }

  return session
}

export const can = (session: Session | null, permission: string) =>
  session?.permissions.includes(permission) ?? false
